import { useCallback, useEffect, useState } from 'react';

export interface CookieConsentState {
  analytics: boolean;
  essential: boolean;
  consentDate: string;
  consentVersion: string;
}

const STORAGE_KEY = 'lily_cookie_consent';

export function useCookieConsent() {
  const [consent, setConsentState] = useState<CookieConsentState | null>(null);

  // Inicializar do localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as CookieConsentState;
        setConsentState(parsed);
      }
    } catch (error) {
      console.error('Failed to parse cookie consent from localStorage', error);
    }
  }, []);

  const getConsent = useCallback((): CookieConsentState | null => {
    if (consent) return consent;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [consent]);

  const setConsent = useCallback((newConsent: CookieConsentState) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newConsent));
      setConsentState(newConsent);

      // Rastrear evento no GA SOMENTE se foi aceito (não reportar rejeições ao GA)
      if (window.gtag && newConsent.analytics) {
        window.gtag('event', 'cookie_consent', {
          analytics_enabled: newConsent.analytics,
          consent_date: newConsent.consentDate,
          consent_version: newConsent.consentVersion,
        });
      }

      // Sincronizar entre abas
      window.dispatchEvent(
        new CustomEvent('cookie_consent_updated', {
          detail: newConsent,
        }),
      );
    } catch (error) {
      // Não quebra a app (ex: modo privado), mas precisa aparecer em log:
      // sem gravar, o banner volta a cada visita e o motivo fica invisível.
      console.error(
        'Não foi possível gravar o consentimento de cookies',
        error,
      );
    }
  }, []);

  const hasConsented = useCallback((): boolean => {
    return getConsent() !== null;
  }, [getConsent]);

  const shouldShowBanner = useCallback((): boolean => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return true;

      const parsed = JSON.parse(stored) as CookieConsentState;
      const currentVersion =
        import.meta.env.VITE_COOKIE_CONSENT_VERSION || '1.0';

      // Mostrar banner novamente se versão mudou (policy update)
      return parsed.consentVersion !== currentVersion;
    } catch {
      return true;
    }
  }, []);

  // Sincronizar entre abas quando consentimento muda em outra aba
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue) as CookieConsentState;
          setConsentState(parsed);
        } catch (error) {
          console.error(
            'Failed to sync cookie consent from storage event',
            error,
          );
        }
      }
    };

    const handleConsentUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<CookieConsentState>;
      setConsentState(customEvent.detail);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cookie_consent_updated', handleConsentUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cookie_consent_updated', handleConsentUpdate);
    };
  }, []);

  return {
    consent,
    getConsent,
    setConsent,
    hasConsented,
    shouldShowBanner,
  };
}

// Tipagem global para window.gtag
declare global {
  interface Window {
    gtag?: (
      command: string,
      action: string,
      config?: Record<string, unknown>,
    ) => void;
    dataLayer?: unknown[];
  }
}
